import React, { useMemo, useRef, useState, useEffect } from 'react';
import { DEFAULT_ENTITY_COLOR } from '../../config/entityColors';

/**
 * Filter the browser by project.
 *
 * This used to be a `FacetSection` like any other — fourth in the list, and
 * built from Algolia's facet buckets. Two consequences made it a poor fit for
 * the question people actually ask:
 *
 *   1. `FacetSection` returns null when it has no buckets, so the ONE control
 *      for "show me this project" disappeared exactly when the current results
 *      had no project — which is the moment you want to pick one.
 *   2. Buckets only cover projects present in the current result set, so a
 *      project you haven't found yet was unreachable by definition.
 *
 * So it reads the workspace's project list instead, and keeps the facet counts
 * as an annotation rather than as the source of truth. It writes the same
 * `metadata.projectIds` facet filter the section did, which is why the active
 * filter chips, "clear all" and the query itself needed no changes at all.
 */
export function ProjectScope({ projects, counts = {}, selected = [], onToggle, onClear, indexReady = true }) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const boxRef = useRef(null);

    // Close on an outside click or Escape — a dropdown that only closes by
    // re-clicking its own button is a dropdown people leave open.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const byId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

    const visible = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        const list = needle ? projects.filter((p) => p.name.toLowerCase().includes(needle)) : projects;
        // Selected first, then whatever the current results actually contain —
        // a project with no matches here is still listed, just not promoted.
        return list.slice().sort((a, b) => {
            const pick = (selected.includes(b.id) ? 1 : 0) - (selected.includes(a.id) ? 1 : 0);
            if (pick) return pick;
            const has = (counts[b.id] || 0) - (counts[a.id] || 0);
            if (has) return has;
            return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        });
    }, [projects, filter, selected, counts]);

    const label = selected.length === 0
        ? 'All projects'
        : selected.length === 1
            ? (byId.get(selected[0])?.name ?? '1 project')
            : `${selected.length} projects`;

    return (
        // A column: the row is the control, and the warning underneath belongs
        // to the whole control rather than sitting inline beside the button.
        <div className="asp-scope-wrap">
        <div className="asp-scope" ref={boxRef}>
            <button
                type="button"
                className={`asp-scope-btn ${selected.length ? 'is-active' : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                // Its text is a project NAME, which doesn't say what pressing it
                // does — and the same name appears on the active-filter chip
                // beside it, so the label is what tells the two apart.
                aria-label={`Filter by project — ${label}`}
            >
                <span className="asp-scope-dot" style={{ background: selected.length === 1 ? (byId.get(selected[0])?.color || DEFAULT_ENTITY_COLOR) : 'transparent' }} />
                <span className="asp-scope-label">{label}</span>
                <span className="asp-scope-caret" aria-hidden="true">▾</span>
            </button>

            {selected.length > 0 && (
                <button type="button" className="asp-scope-clear" onClick={onClear} aria-label="Show all projects">
                    ×
                </button>
            )}

            {open && (
                <div className="asp-scope-menu" role="listbox" aria-label="Filter by project">
                    {projects.length > 8 && (
                        <input
                            className="asp-scope-search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Find a project…"
                            aria-label="Find a project"
                            autoFocus
                        />
                    )}

                    <button
                        type="button"
                        className={`asp-scope-item ${selected.length === 0 ? 'is-selected' : ''}`}
                        onClick={() => { onClear(); setOpen(false); }}
                        role="option"
                        aria-selected={selected.length === 0}
                    >
                        <span className="asp-scope-dot" />
                        <span className="asp-scope-name">All projects</span>
                    </button>

                    {projects.length === 0 && (
                        <p className="asp-scope-empty">No projects in this workspace yet.</p>
                    )}
                    {projects.length > 0 && visible.length === 0 && (
                        <p className="asp-scope-empty">No project name contains that.</p>
                    )}

                    {visible.map((project) => {
                        const on = selected.includes(project.id);
                        const count = counts[project.id];
                        return (
                            <button
                                key={project.id}
                                type="button"
                                className={`asp-scope-item ${on ? 'is-selected' : ''}`}
                                onClick={() => onToggle('metadata.projectIds', project.id)}
                                role="option"
                                aria-selected={on}
                            >
                                <span className="asp-scope-dot" style={{ background: project.color || DEFAULT_ENTITY_COLOR }} />
                                <span className="asp-scope-name">{project.name}</span>
                                {/* The count is what these results hold, not what the
                                    project holds — absent simply means "none here". */}
                                {count > 0 && <span className="asp-scope-count">{count}</span>}
                                {on && <span className="asp-scope-tick" aria-hidden="true">✓</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>

        {/* The failure this whole flag exists for: Algolia answers a filter on
            an UNREGISTERED attribute with zero hits rather than an error — so a
            misconfigured index looks exactly like an empty project, and stays
            that way until somebody queries the index directly. */}
        {!indexReady && (
            <p className="asp-scope-warn" role="status">
                The search index isn’t set up to filter by project yet, so this filter returns
                nothing. Run <code>npm run algolia:settings</code> on the backend.
            </p>
        )}
        </div>
    );
}

export default ProjectScope;
